import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  FaLink, FaQrcode, FaExchangeAlt, FaKey, FaCode, FaTags,
  FaCopy, FaCheck, FaArrowLeft, FaTools, FaDownload, FaRedo,
  FaEye, FaEyeSlash
} from 'react-icons/fa';
import { useLang } from '../context/LangContext';

/* ─── Shared copy helper ─── */
function useCopy() {
  const [copied, setCopied] = useState(false);
  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return { copied, copy };
}

/* ═══════════════════════════════════════
   1. UTM Link Builder
   ═══════════════════════════════════════ */
function UTMBuilder() {
  const { t } = useLang();
  const [url, setUrl] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('');
  const [term, setTerm] = useState('');
  const [content, setContent] = useState('');
  const { copied, copy } = useCopy();

  const buildUrl = () => {
    if (!url) return '';
    try {
      const u = new URL(url.startsWith('http') ? url : `https://${url}`);
      if (source) u.searchParams.set('utm_source', source);
      if (medium) u.searchParams.set('utm_medium', medium);
      if (campaign) u.searchParams.set('utm_campaign', campaign);
      if (term) u.searchParams.set('utm_term', term);
      if (content) u.searchParams.set('utm_content', content);
      return u.toString();
    } catch {
      return '';
    }
  };

  const result = buildUrl();

  return (
    <div className="tool-panel">
      <div className="tool-field">
        <label>{t('websiteUrl')} *</label>
        <input type="text" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com" />
      </div>
      <div className="tool-field-row">
        <div className="tool-field">
          <label>{t('source')} *</label>
          <input type="text" value={source} onChange={e => setSource(e.target.value)} placeholder="google, facebook, newsletter" />
        </div>
        <div className="tool-field">
          <label>{t('medium')} *</label>
          <input type="text" value={medium} onChange={e => setMedium(e.target.value)} placeholder="cpc, email, social" />
        </div>
      </div>
      <div className="tool-field">
        <label>{t('campaign')} *</label>
        <input type="text" value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="spring_sale, product_launch" />
      </div>
      <div className="tool-field-row">
        <div className="tool-field">
          <label>{t('term')}</label>
          <input type="text" value={term} onChange={e => setTerm(e.target.value)} placeholder="keyword" />
        </div>
        <div className="tool-field">
          <label>{t('content')}</label>
          <input type="text" value={content} onChange={e => setContent(e.target.value)} placeholder="banner_v1" />
        </div>
      </div>
      {result && (
        <div className="tool-result">
          <label>{t('generatedUrl')}</label>
          <div className="tool-result-box">
            <code>{result}</code>
            <button className="tool-copy-btn" onClick={() => copy(result)}>
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   2. QR Code Generator
   ═══════════════════════════════════════ */
function QRGenerator() {
  const { t } = useLang();
  const [text, setText] = useState('');
  const [size, setSize] = useState(256);

  const qrUrl = text
    ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=0c0e1a&color=e8ecf4`
    : '';

  const download = () => {
    if (!qrUrl) return;
    const a = document.createElement('a');
    a.href = qrUrl;
    a.download = 'qrcode.png';
    a.click();
  };

  return (
    <div className="tool-panel">
      <div className="tool-field">
        <label>{t('textOrUrl')}</label>
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder={t('enterTextQr')} rows={3} />
      </div>
      <div className="tool-field">
        <label>{t('size')}: {size}px</label>
        <input type="range" min={128} max={512} step={64} value={size} onChange={e => setSize(Number(e.target.value))} />
      </div>
      {qrUrl && (
        <div className="tool-result tool-qr-result">
          <div className="tool-qr-preview">
            <img src={qrUrl} alt="QR Code" />
          </div>
          <button className="tool-action-btn" onClick={download}>
            <FaDownload /> {t('downloadPng')}
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   3. Currency Converter
   ═══════════════════════════════════════ */
const CURRENCIES = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'KRW', 'HKD', 'TWD', 'SGD', 'AUD', 'CAD', 'CHF', 'INR', 'BRL', 'RUB', 'THB', 'VND', 'MYR', 'PHP', 'IDR'];

function CurrencyConverter() {
  const { t } = useLang();
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('CNY');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const convert = async () => {
    if (!amount || isNaN(amount)) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${from}`);
      const data = await res.json();
      const rate = data.rates[to];
      if (rate) {
        setResult({ rate, converted: (parseFloat(amount) * rate).toFixed(4) });
      } else {
        setError('Currency not supported');
      }
    } catch {
      setError('Failed to fetch exchange rates. Try again.');
    }
    setLoading(false);
  };

  const swap = () => {
    setFrom(to);
    setTo(from);
    setResult(null);
  };

  return (
    <div className="tool-panel">
      <div className="tool-field">
        <label>{t('amount')}</label>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="1.00" />
      </div>
      <div className="tool-currency-row">
        <div className="tool-field">
          <label>{t('from')}</label>
          <select value={from} onChange={e => { setFrom(e.target.value); setResult(null); }}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <button className="tool-swap-btn" onClick={swap}><FaExchangeAlt /></button>
        <div className="tool-field">
          <label>{t('to')}</label>
          <select value={to} onChange={e => { setTo(e.target.value); setResult(null); }}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <button className="tool-action-btn" onClick={convert} disabled={loading}>
        {loading ? t('converting') : t('convert')}
      </button>
      {error && <div className="tool-error">{error}</div>}
      {result && (
        <div className="tool-result">
          <div className="tool-currency-result">
            <span className="currency-amount">{parseFloat(amount).toLocaleString()} {from}</span>
            <span className="currency-equals">=</span>
            <span className="currency-converted">{parseFloat(result.converted).toLocaleString(undefined, { maximumFractionDigits: 4 })} {to}</span>
          </div>
          <div className="currency-rate">{t('rate')}: 1 {from} = {result.rate} {to}</div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   4. Password Generator
   ═══════════════════════════════════════ */
function PasswordGenerator() {
  const { t } = useLang();
  const [length, setLength] = useState(16);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [numbers, setNumbers] = useState(true);
  const [symbols, setSymbols] = useState(true);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const { copied, copy } = useCopy();

  const generate = () => {
    let chars = '';
    if (upper) chars += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    if (lower) chars += 'abcdefghijklmnopqrstuvwxyz';
    if (numbers) chars += '0123456789';
    if (symbols) chars += '!@#$%^&*()_+-=[]{}|;:,.<>?';
    if (!chars) chars = 'abcdefghijklmnopqrstuvwxyz';

    const arr = new Uint32Array(length);
    crypto.getRandomValues(arr);
    setPassword(Array.from(arr, v => chars[v % chars.length]).join(''));
  };

  const getStrength = () => {
    if (!password) return { label: '', color: '', width: '0%' };
    let pool = 0;
    if (upper) pool += 26;
    if (lower) pool += 26;
    if (numbers) pool += 10;
    if (symbols) pool += 26;
    const entropy = Math.log2(pool) * password.length;
    if (entropy < 40) return { label: t('weak'), color: '#ef4444', width: '25%' };
    if (entropy < 60) return { label: t('fair'), color: '#f59e0b', width: '50%' };
    if (entropy < 80) return { label: t('strong'), color: '#22d3ee', width: '75%' };
    return { label: t('veryStrong'), color: '#10b981', width: '100%' };
  };

  const strength = getStrength();

  return (
    <div className="tool-panel">
      <div className="tool-field">
        <label>{t('length')}: {length}</label>
        <input type="range" min={6} max={64} value={length} onChange={e => setLength(Number(e.target.value))} />
      </div>
      <div className="tool-checkboxes">
        <label><input type="checkbox" checked={upper} onChange={e => setUpper(e.target.checked)} /> {t('uppercase')}</label>
        <label><input type="checkbox" checked={lower} onChange={e => setLower(e.target.checked)} /> {t('lowercase')}</label>
        <label><input type="checkbox" checked={numbers} onChange={e => setNumbers(e.target.checked)} /> {t('numbers')}</label>
        <label><input type="checkbox" checked={symbols} onChange={e => setSymbols(e.target.checked)} /> {t('symbols')}</label>
      </div>
      <button className="tool-action-btn" onClick={generate}><FaRedo /> {t('generate')}</button>
      {password && (
        <div className="tool-result">
          <div className="tool-result-box tool-password-box">
            <code>{showPwd ? password : '•'.repeat(password.length)}</code>
            <div className="tool-password-actions">
              <button onClick={() => setShowPwd(!showPwd)}>{showPwd ? <FaEyeSlash /> : <FaEye />}</button>
              <button onClick={() => copy(password)}>{copied ? <FaCheck /> : <FaCopy />}</button>
            </div>
          </div>
          <div className="tool-strength-bar">
            <div className="strength-fill" style={{ width: strength.width, background: strength.color }} />
          </div>
          <span className="tool-strength-label" style={{ color: strength.color }}>{strength.label}</span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   5. Base64 Encoder / Decoder
   ═══════════════════════════════════════ */
function Base64Tool() {
  const { t } = useLang();
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('encode');
  const { copied, copy } = useCopy();

  const process = () => {
    try {
      if (mode === 'encode') {
        setOutput(btoa(unescape(encodeURIComponent(input))));
      } else {
        setOutput(decodeURIComponent(escape(atob(input))));
      }
    } catch {
      setOutput('Error: Invalid input');
    }
  };

  return (
    <div className="tool-panel">
      <div className="tool-mode-toggle">
        <button className={mode === 'encode' ? 'active' : ''} onClick={() => { setMode('encode'); setOutput(''); }}>{t('encode')}</button>
        <button className={mode === 'decode' ? 'active' : ''} onClick={() => { setMode('decode'); setOutput(''); }}>{t('decode')}</button>
      </div>
      <div className="tool-field">
        <label>{mode === 'encode' ? t('plainText') : t('base64String')}</label>
        <textarea value={input} onChange={e => setInput(e.target.value)} rows={4} placeholder={mode === 'encode' ? t('enterTextEncode') : t('enterBase64Decode')} />
      </div>
      <button className="tool-action-btn" onClick={process}>
        <FaCode /> {mode === 'encode' ? t('encode') : t('decode')}
      </button>
      {output && (
        <div className="tool-result">
          <label>{t('result')}</label>
          <div className="tool-result-box">
            <code>{output}</code>
            <button className="tool-copy-btn" onClick={() => copy(output)}>
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   6. SEO Meta Tag Generator
   ═══════════════════════════════════════ */
function MetaTagGenerator() {
  const { t } = useLang();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [ogImage, setOgImage] = useState('');
  const [url, setUrl] = useState('');
  const { copied, copy } = useCopy();

  const tags = `<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${title || 'Your Page Title'}</title>
<meta name="description" content="${description}">
<meta name="keywords" content="${keywords}">

<!-- Open Graph -->
<meta property="og:title" content="${title}">
<meta property="og:description" content="${description}">
<meta property="og:type" content="website">${url ? `\n<meta property="og:url" content="${url}">` : ''}${ogImage ? `\n<meta property="og:image" content="${ogImage}">` : ''}

<!-- Twitter Card -->
<meta name="twitter:card" content="summary_large_image">
<meta name="twitter:title" content="${title}">
<meta name="twitter:description" content="${description}">${ogImage ? `\n<meta name="twitter:image" content="${ogImage}">` : ''}`;

  return (
    <div className="tool-panel">
      <div className="tool-field">
        <label>{t('pageTitle')} <span className="tool-counter">{title.length}/60</span></label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="My Awesome Page" maxLength={60} />
      </div>
      <div className="tool-field">
        <label>{t('description')} <span className="tool-counter">{description.length}/160</span></label>
        <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="A brief description of your page..." rows={3} maxLength={160} />
      </div>
      <div className="tool-field">
        <label>{t('keywords')}</label>
        <input type="text" value={keywords} onChange={e => setKeywords(e.target.value)} placeholder="seo, tools, marketing" />
      </div>
      <div className="tool-field-row">
        <div className="tool-field">
          <label>{t('pageUrl')}</label>
          <input type="text" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com" />
        </div>
        <div className="tool-field">
          <label>{t('ogImage')}</label>
          <input type="text" value={ogImage} onChange={e => setOgImage(e.target.value)} placeholder="https://example.com/image.jpg" />
        </div>
      </div>
      {title && (
        <div className="tool-result">
          <div className="tool-meta-preview">
            <div className="meta-preview-title">{title}</div>
            <div className="meta-preview-url">{url || 'https://example.com'}</div>
            <div className="meta-preview-desc">{description || 'No description provided.'}</div>
          </div>
          <label>{t('generatedMeta')}</label>
          <div className="tool-result-box tool-code-block">
            <pre>{tags}</pre>
            <button className="tool-copy-btn" onClick={() => copy(tags)}>
              {copied ? <FaCheck /> : <FaCopy />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   Tool Definitions & Main Component
   ═══════════════════════════════════════ */
const TOOLS = [
  { id: 'utm', nameKey: 'utmName', descKey: 'utmDesc', icon: FaLink, color: '#6366f1', component: UTMBuilder },
  { id: 'qr', nameKey: 'qrName', descKey: 'qrDesc', icon: FaQrcode, color: '#22d3ee', component: QRGenerator },
  { id: 'currency', nameKey: 'currencyName', descKey: 'currencyDesc', icon: FaExchangeAlt, color: '#34d399', component: CurrencyConverter },
  { id: 'password', nameKey: 'passwordName', descKey: 'passwordDesc', icon: FaKey, color: '#f472b6', component: PasswordGenerator },
  { id: 'base64', nameKey: 'base64Name', descKey: 'base64Desc', icon: FaCode, color: '#fbbf24', component: Base64Tool },
  { id: 'meta', nameKey: 'metaName', descKey: 'metaDesc', icon: FaTags, color: '#a855f7', component: MetaTagGenerator },
];

function Tools() {
  const { t } = useLang();
  const [activeTool, setActiveTool] = useState(null);

  const active = TOOLS.find(tool => tool.id === activeTool);

  return (
    <div className="tools-page">
      <header className="tools-header">
        <Link to="/" className="tools-back-btn"><FaArrowLeft /> {t('back')}</Link>
        <div className="tools-header-center">
          <FaTools className="tools-header-icon" />
          <h1>{t('toolsTitle')}</h1>
          <p>{t('toolsDesc')}</p>
        </div>
      </header>

      {!activeTool ? (
        <div className="tools-grid">
          {TOOLS.map(tool => (
            <button key={tool.id} className="tool-card" onClick={() => setActiveTool(tool.id)}>
              <div className="tool-card-icon" style={{ background: `${tool.color}18`, color: tool.color }}>
                <tool.icon />
              </div>
              <h3>{t(tool.nameKey)}</h3>
              <p>{t(tool.descKey)}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="tool-workspace">
          <div className="tool-workspace-header">
            <button className="tool-back" onClick={() => setActiveTool(null)}>
              <FaArrowLeft /> {t('allTools')}
            </button>
            <div className="tool-workspace-title">
              <div className="tool-workspace-icon" style={{ background: `${active.color}18`, color: active.color }}>
                <active.icon />
              </div>
              <div>
                <h2>{t(active.nameKey)}</h2>
                <p>{t(active.descKey)}</p>
              </div>
            </div>
          </div>
          <active.component />
        </div>
      )}
    </div>
  );
}

export default Tools;
